import { createElement } from 'react';
import { createRoute, type AnyRoute } from '@tanstack/react-router';
import { ContratoDetalhePage } from '@/features/contratos/ContratoDetalhePage';
import { ContratosPage } from '@/features/contratos/ContratosPage';
import { CAMINHO_DETALHE, CAMINHO_LISTA } from '@/features/contratos/navegacao';

/**
 * Rotas das telas de contrato.
 *
 * Exportadas como **fábrica**, e não como objetos prontos, pelo mesmo motivo
 * de `features/tickets/routes.ts`, `features/admin/routes.ts` e
 * `features/clientes/routes.ts`: `createRoute` exige a rota pai, e o pai
 * (`appRoute`, com o `AppShell` e a guarda de sessão) mora em
 * `app/router.tsx`, que pertence a outro dono. Receber o pai por parâmetro
 * deixa a integração ser uma linha lá, sem que esta feature importe de dentro
 * de `app/` — o que fecharia um ciclo entre o roteador e a tela que ele monta.
 *
 * Duas rotas irmãs, e não uma raiz com filhas: lista e ficha não compartilham
 * casca. A ficha é uma tela inteira, com cabeçalho próprio e caminho de volta.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  Integração esperada em `app/router.tsx` — UMA linha, mais o `addChildren`
 * ─────────────────────────────────────────────────────────────────────────
 *
 * ```ts
 * import { criarRotasDeContratos } from '@/features/contratos/routes';
 *
 * const { lista: contratosRoute, detalhe: contratoRoute } =
 *   criarRotasDeContratos(appRoute);
 *
 * appRoute.addChildren([indexRoute, contratosRoute, contratoRoute, …]);
 * ```
 *
 * E em `app/nav.ts`, o item de menu — a permissão que o guarda é
 * `contract.manage`:
 *
 * ```ts
 * { to: CAMINHO_LISTA, rotulo: 'Contratos', permissao: 'contract.manage' }
 * ```
 *
 * Nenhum dos dois arquivos é tocado por esta feature: eles são de outro dono
 * nesta rodada.
 */
export function criarRotasDeContratos<TRotaPai extends AnyRoute>(rotaPai: TRotaPai) {
  const lista = createRoute({
    getParentRoute: () => rotaPai,
    path: CAMINHO_LISTA,
    component: ContratosPage,
  });

  const detalhe = createRoute({
    getParentRoute: () => rotaPai,
    path: CAMINHO_DETALHE,
    // `createElement` em vez de JSX para este arquivo continuar sendo `.ts`, e
    // a conversão do parâmetro porque o roteador só infere `{ contratoId }`
    // depois que a árvore estiver registrada em `app/router.tsx`. Mesma
    // mecânica de `features/clientes/routes.ts`.
    component: () => {
      const { contratoId } = detalhe.useParams() as { contratoId: string };
      return createElement(ContratoDetalhePage, { contratoId });
    },
  });

  return { lista, detalhe };
}

export { CAMINHO_DETALHE, CAMINHO_LISTA };
